import { useState, useCallback, useEffect } from 'react';
import { FilterOperator, type TeamMembersQueryParams } from '../../../apiHooks/useTeamMembersQuery';
import { useDebounce } from '../../../lib/hooks/useDebounce';
import { useZustandStore } from '../../../appState/StoresProvider';

export const useTeamMembersFilters = (onNewParams: (params: Partial<TeamMembersQueryParams>) => void) => {
  const [nameSearchInputValue, setNameSearchInputValue] = useState<string>('');
  const debounceOnNewParams = useDebounce(onNewParams, 500);
  const selectedBehaviorFilter = useZustandStore('teamBehaviors', (v) => v.selectedBehaviorFilter);
  const clearSelectedBehaviorFilter = useZustandStore('teamBehaviors', (v) => v.clearSelectedBehaviorFilter);

  const handleTeamBehaviorFiltersChange = useCallback(() => {
    const newFilters = selectedBehaviorFilter
      ? [
          {
            property: 'behaviourType',
            operator: FilterOperator.Equals,
            value: selectedBehaviorFilter.name,
          },
          {
            property: 'behaviour',
            operator: FilterOperator.Equals,
            value: selectedBehaviorFilter.type,
          },
        ]
      : [];

    onNewParams({ filters: newFilters });
  }, [onNewParams, selectedBehaviorFilter]);

  useEffect(() => {
    handleTeamBehaviorFiltersChange();
  }, [handleTeamBehaviorFiltersChange, selectedBehaviorFilter]);

  const handleNameSearchChange = useCallback(
    (searchTerm: string) => {
      setNameSearchInputValue(searchTerm);
      const newFilters = [{ property: 'name', operator: FilterOperator.Contains, value: searchTerm }];
      debounceOnNewParams({ filters: newFilters });
    },
    [debounceOnNewParams],
  );

  const clearFilters = useCallback(() => {
    setNameSearchInputValue('');
    clearSelectedBehaviorFilter();
    onNewParams({ filters: [] });
  }, [clearSelectedBehaviorFilter, onNewParams]);

  return { nameSearchInputValue, handleNameSearchChange, selectedBehaviorFilter, clearFilters };
};
